import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Truck, ArrowLeft, Mail, User, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotUsernamePage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-username`, { email });
      setResult(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No account found with this email address');
      } else {
        toast.error('Failed to look up account. Please try again.');
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/fleetcost-logo.png" alt="FleetCost.App" className="h-16 mx-auto object-contain mb-2" data-testid="forgot-username-logo" />
          <p className="text-slate-500 mt-1">Account Recovery</p>
        </div>

        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="font-heading text-2xl font-bold text-gray-800">
              {result ? 'Account Found!' : 'Forgot Username'}
            </CardTitle>
            <CardDescription className="text-gray-500">
              {result 
                ? 'Here are your account details'
                : 'Enter your email to retrieve your account name'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!result ? (
              <form onSubmit={handleLookup} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm font-medium">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 pl-10 border-gray-300"
                      data-testid="username-email-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gray-800 hover:bg-gray-700"
                  disabled={loading}
                  data-testid="lookup-username-btn"
                >
                  {loading ? 'Looking up...' : 'Find My Account'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-800 font-medium">Account found!</p>
                      <p className="text-xs text-green-600 mt-1">Your account details are below</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Account Name</p>
                      <p className="font-medium text-gray-800">{result.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-800">{result.email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <Link to="/login">
                    <Button className="w-full h-11 bg-gray-800 hover:bg-gray-700" data-testid="go-login-btn">
                      Go to Login
                    </Button>
                  </Link>
                  <Link to="/forgot-password">
                    <Button variant="outline" className="w-full h-11">
                      Reset Password Instead
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Back to Login Link */}
            {!result && (
              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotUsernamePage;
